/**
 * VoiceProcessor — Real-time voice processing chain using native Web Audio API.
 * Provides: high-pass filter, compressor, voice EQ (presence + air),
 * de-esser, and auto-gain normalization.
 *
 * Usage:
 *   await VoiceProcessor.warm(audioContext);   // once per context, before createChain
 *   var chain = VoiceProcessor.createChain(audioContext, settings);
 *   sourceNode.connect(chain.input);
 *   chain.output.connect(destination);
 *   // Update settings live:
 *   chain.update({ comp_enabled: false });
 *   // Cleanup:
 *   chain.destroy();
 *
 * ── Голова цепочки — ворклет finite-gate, а не GainNode ─────────────────────
 * Sentry TIMBRICA-FRONTEND-MH (/ru/dictaphone): «setTargetAtTime … The provided
 * float value is non-finite» в автоусилении. Цель была NaN, потому что NaN шёл
 * в самом сигнале: ОДИН не-конечный сэмпл с устройства на входе BiquadFilterNode
 * делает его выход не-конечным до конца рендера и не восстанавливается (замер в
 * Chromium, OfflineAudioContext; компрессор и Gain — восстанавливаются), а
 * высокочастотный фильтр здесь включён всегда. Итог: половина дубля — NaN, и
 * без автоусиления это происходило МОЛЧА. Ворклет (public/js/finite-gate-worklet.js)
 * заменяет NaN/±Inf нулём, конечное пропускает побитно, задержки не добавляет.
 * Его модуль грузится один раз на контекст — `warm(ctx)`; адрес публикует
 * components/voice-processing-panel (`window.__FINITE_GATE_WORKLET_URL`, версия
 * в пути через asset-router). Не прогретый контекст получает прежний GainNode —
 * цепочка собирается всегда. Сторожа: tests/js/voice-chain-non-finite-input.test.mjs,
 * tests/playwright/voice-chain-survives-a-nonfinite-sample.mjs.
 */
window.VoiceProcessor = (function() {
    'use strict';

    // Каждое из этих событий — один раз на страницу: считаем сессии, а не дубли.
    var _told = {};
    function reportOnce(action, extra) {
        if (_told[action]) return;
        _told[action] = true;
        try {
            if (window.__ae) {
                var p = { action: action };
                if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) p[k] = extra[k];
                window.__ae('feature_use', p);
            }
        } catch (_) {}
    }

    var DEFAULTS = {
        highpass_enabled: true,
        highpass_freq: 80,

        comp_enabled: true,
        comp_threshold: -24,
        comp_ratio: 4,

        eq_enabled: false,
        eq_presence: 3,    // dB at 3kHz
        eq_air: 2,         // dB at 12kHz

        deesser_enabled: false,
        deesser_amount: -4, // dB cut at 6.5kHz

        autogain_enabled: false,
    };

    function mergeOpts(a, b) {
        var r = {};
        for (var k in a) r[k] = a[k];
        if (b) for (var k in b) if (b.hasOwnProperty(k)) r[k] = b[k];
        return r;
    }

    /** The finite gate, when this context is warm; null → the caller keeps a GainNode. */
    function makeGate(ctx) {
        if (!ctx || !ctx.__vpGateReady || typeof AudioWorkletNode === 'undefined') return null;
        try {
            var node = new AudioWorkletNode(ctx, 'finite-gate', { numberOfInputs: 1, numberOfOutputs: 1 });
            node.port.onmessage = function(ev) {
                var d = ev && ev.data;
                if (d && d.nonFinite) reportOnce('audio_input_nonfinite', { where: 'vp', n: d.nonFinite });
            };
            return node;
        } catch (e) {
            reportOnce('finite_gate_failed', { reason: String(e && e.message || e).slice(0, 80) });
            return null;
        }
    }

    return {
        DEFAULTS: DEFAULTS,

        /**
         * Load the finite gate (public/js/finite-gate-worklet.js) on this
         * context — once; createChain(ctx) then puts it at the head of the
         * chain. Same shape as RNNoiseRealtime.warmWorklet: callers await it
         * right before createChain.
         * @param {BaseAudioContext} ctx
         * @returns {Promise<boolean>} true = createChain(ctx) gets the gate
         */
        warm: function(ctx) {
            var url = window.__FINITE_GATE_WORKLET_URL;
            if (!url || !ctx || !ctx.audioWorklet || typeof AudioWorkletNode === 'undefined') return Promise.resolve(false);
            if (ctx.__vpGateReady) return Promise.resolve(true);
            if (!ctx.__vpGateWarming) {
                ctx.__vpGateWarming = ctx.audioWorklet.addModule(url).then(function() {
                    ctx.__vpGateReady = true;
                    return true;
                }, function(e) {
                    ctx.__vpGateWarming = null;
                    reportOnce('finite_gate_failed', { reason: String(e && e.message || e).slice(0, 80) });
                    return false;
                });
            }
            return ctx.__vpGateWarming;
        },

        /**
         * Create a processing chain.
         * @param {AudioContext} ctx
         * @param {Object} [opts] - Override defaults
         * @returns {{ input: AudioWorkletNode|GainNode, output: GainNode, update: Function, destroy: Function }}
         */
        createChain: function(ctx, opts) {
            var p = mergeOpts(DEFAULTS, opts);

            // Input / output nodes (always present, act as routing anchors).
            // The input is the finite gate when warm(ctx) ran — see the header.
            var gate = makeGate(ctx);
            var input = gate || ctx.createGain();
            var output = ctx.createGain();

            // === High-pass filter ===
            var highpass = ctx.createBiquadFilter();
            highpass.type = 'highpass';
            highpass.frequency.value = p.highpass_freq;
            highpass.Q.value = 0.707;

            // === Compressor ===
            var compressor = ctx.createDynamicsCompressor();
            compressor.threshold.value = p.comp_threshold;
            compressor.ratio.value = p.comp_ratio;
            compressor.attack.value = 0.003;
            compressor.release.value = 0.25;
            compressor.knee.value = 6;

            // === EQ: presence (3kHz) + air (12kHz) ===
            var eqPresence = ctx.createBiquadFilter();
            eqPresence.type = 'peaking';
            eqPresence.frequency.value = 3000;
            eqPresence.Q.value = 1.2;
            eqPresence.gain.value = p.eq_presence;

            var eqAir = ctx.createBiquadFilter();
            eqAir.type = 'highshelf';
            eqAir.frequency.value = 12000;
            eqAir.gain.value = p.eq_air;

            // === De-esser (simplified: peaking cut at 6.5kHz) ===
            var deesser = ctx.createBiquadFilter();
            deesser.type = 'peaking';
            deesser.frequency.value = 6500;
            deesser.Q.value = 2.0;
            deesser.gain.value = p.deesser_amount;

            // === Auto-gain ===
            var autoGainNode = ctx.createGain();
            autoGainNode.gain.value = 1.0;
            var agAnalyser = ctx.createAnalyser();
            agAnalyser.fftSize = 2048;
            var agData = new Float32Array(agAnalyser.fftSize);
            var agInterval = null;
            var AG_TARGET = -18; // dBFS
            var AG_MIN = 0.5;
            var AG_MAX = 6.0;
            var AG_SMOOTH = 0.1; // smoothing factor per tick

            function startAutoGain() {
                if (agInterval) return;
                agInterval = setInterval(function() {
                    agAnalyser.getFloatTimeDomainData(agData);
                    // Calculate RMS
                    var sum = 0;
                    for (var i = 0; i < agData.length; i++) sum += agData[i] * agData[i];
                    var rms = Math.sqrt(sum / agData.length);
                    // Не-конечный уровень до этой строки не должен доходить никогда:
                    // голова цепочки — finite gate. Если дошёл (контекст не прогрет,
                    // ворклет не поднялся), AudioParam он всё равно не получит —
                    // иначе TypeError из setTargetAtTime каждые 150 мс
                    // (TIMBRICA-FRONTEND-MH). Сообщается как нарушение инварианта.
                    if (!isFinite(rms)) { reportOnce('vp_autogain_nonfinite', { gate: gate ? 1 : 0 }); return; }
                    if (rms < 0.0001) return; // silence, don't adjust

                    var rmsDb = 20 * Math.log10(rms);
                    var diff = AG_TARGET - rmsDb;
                    var targetGain = Math.pow(10, diff / 20);
                    targetGain = Math.max(AG_MIN, Math.min(AG_MAX, targetGain));

                    // Smooth approach
                    var current = autoGainNode.gain.value;
                    var next = current + (targetGain - current) * AG_SMOOTH;
                    autoGainNode.gain.setTargetAtTime(next, ctx.currentTime, 0.05);
                }, 150);
            }

            function stopAutoGain() {
                if (agInterval) { clearInterval(agInterval); agInterval = null; }
                autoGainNode.gain.value = 1.0;
            }

            // === Routing ===
            // Build chain: input → [highpass] → [compressor] → [eqPresence→eqAir] → [deesser] → [autoGain] → output
            // When effect is off, it's bypassed.

            function rebuild() {
                // Disconnect everything
                input.disconnect();
                highpass.disconnect();
                compressor.disconnect();
                eqPresence.disconnect();
                eqAir.disconnect();
                deesser.disconnect();
                autoGainNode.disconnect();
                agAnalyser.disconnect();

                // Build chain
                var chain = [input];

                if (p.highpass_enabled) chain.push(highpass);
                if (p.comp_enabled) chain.push(compressor);
                if (p.eq_enabled) { chain.push(eqPresence); chain.push(eqAir); }
                if (p.deesser_enabled) chain.push(deesser);

                if (p.autogain_enabled) {
                    chain.push(autoGainNode);
                    // Tap analyser from the node before autoGain
                    var tapIdx = chain.length - 2;
                    if (tapIdx >= 0) chain[tapIdx].connect(agAnalyser);
                    startAutoGain();
                } else {
                    stopAutoGain();
                }

                chain.push(output);

                // Connect sequentially
                for (var i = 0; i < chain.length - 1; i++) {
                    chain[i].connect(chain[i + 1]);
                }
            }

            rebuild();

            return {
                input: input,
                output: output,

                /**
                 * Update one or more parameters. Triggers chain rebuild if enable/disable changed.
                 * @param {Object} newParams
                 */
                update: function(newParams) {
                    var needRebuild = false;
                    for (var k in newParams) {
                        if (!newParams.hasOwnProperty(k)) continue;
                        var v = newParams[k];
                        // Detect enable/disable changes
                        if (k.indexOf('_enabled') !== -1 && p[k] !== v) needRebuild = true;
                        p[k] = v;
                    }

                    // Apply continuous params without rebuild
                    highpass.frequency.value = p.highpass_freq;
                    compressor.threshold.value = p.comp_threshold;
                    compressor.ratio.value = p.comp_ratio;
                    eqPresence.gain.value = p.eq_presence;
                    eqAir.gain.value = p.eq_air;
                    deesser.gain.value = p.deesser_amount;

                    if (needRebuild) rebuild();
                },

                /** Get current parameters */
                getParams: function() { return mergeOpts(p, {}); },

                /** Destroy chain and free resources */
                destroy: function() {
                    stopAutoGain();
                    // The gate's process() keeps it alive until told to stop.
                    if (gate) { try { gate.port.postMessage({ cmd: 'stop' }); } catch(e) {} }
                    try { input.disconnect(); } catch(e) {}
                    try { highpass.disconnect(); } catch(e) {}
                    try { compressor.disconnect(); } catch(e) {}
                    try { eqPresence.disconnect(); } catch(e) {}
                    try { eqAir.disconnect(); } catch(e) {}
                    try { deesser.disconnect(); } catch(e) {}
                    try { autoGainNode.disconnect(); } catch(e) {}
                    try { agAnalyser.disconnect(); } catch(e) {}
                }
            };
        }
    };
})();
